import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    title: "Information We Collect",
    body: [
      "Account details you provide when you sign up — your name, mobile number, email address, and (if you register as a partner) identity and skill verification documents.",
      "Booking details — the service you book, your address or service location, preferred date/time, and any notes or photos you share for the job.",
      "Payment information — processed securely through our payment gateway partners. We do not store your full card, UPI, or bank details on our own servers.",
      "Location data — your city and address, used to match you with nearby service partners and to calculate applicable pricing.",
      "Usage data — pages you visit, services you view, and device/browser information, used to improve the app and troubleshoot issues.",
    ],
  },
  {
    title: "How We Use Your Information",
    body: [
      "To create and manage your account, and to process and fulfil your bookings.",
      "To match you with a suitable, available service partner in your area.",
      "To send booking confirmations, status updates, and support communication via SMS, email, or in-app notifications.",
      "To calculate pricing, process payments and refunds, and resolve cancellations or disputes.",
      "To improve our services, and — where you've opted in — to send offers and promotions.",
    ],
  },
  {
    title: "How We Share Your Information",
    body: [
      "With the service partner assigned to your booking — only what's needed to complete the job (your name, address, contact number, and booking details).",
      "With payment gateway and logistics partners, solely to process payments or fulfil product orders.",
      "We do not sell your personal information to third parties for advertising.",
      "We may share information if required by law, or to protect the safety of our users and partners.",
    ],
  },
  {
    title: "Data Security",
    body: [
      "We use industry-standard measures to protect your data, including encrypted connections and access controls on our systems.",
      "No method of transmission or storage is 100% secure; we work to continuously improve our safeguards.",
    ],
  },
  {
    title: "Your Choices & Rights",
    body: [
      "You can review and update your profile information anytime from your account settings.",
      "You can request deletion of your account and associated personal data by contacting support.",
      "You can opt out of promotional messages while continuing to receive essential booking-related communication.",
    ],
  },
  {
    title: "Cookies & Local Storage",
    body: [
      "We use cookies and browser local storage to keep you logged in, remember your cart, and understand how the app is used — not to track you across other websites.",
    ],
  },
  {
    title: "Children's Privacy",
    body: [
      "Our services are intended for users who are 18 years or older. We do not knowingly collect personal information from children.",
    ],
  },
  {
    title: "Changes to This Policy",
    body: [
      "We may update this policy from time to time to reflect changes in our practices. The \"last updated\" date below will always show the most recent revision.",
    ],
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Privacy Policy</div>

        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.intro}>
          This policy explains what information ClickNido collects when you use our
          platform to book home, technical, caretaker, outdoor, transport, and special
          services, and how that information is used, shared, and protected.
        </p>
        <p style={styles.updated}>Last updated: August 2026</p>

        {sections.map((sec) => (
          <div key={sec.title} style={styles.card}>
            <h2 style={styles.cardTitle}>{sec.title}</h2>
            <ul style={styles.list}>
              {sec.body.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ))}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Contact Us</h2>
          <p style={styles.contactText}>
            Questions about this policy, or want to update or delete your data? Reach out
            to us anytime.
          </p>
          <button style={styles.inlineLink} onClick={() => navigate("/contact-us")}>
            Contact Us <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
  },
  breadcrumb: {
    color: "#7b8198",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 800,
    color: "#1c2148",
    marginBottom: "12px",
  },
  intro: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.75,
    color: "#5c6480",
    maxWidth: "700px",
  },
  updated: {
    margin: 0,
    fontSize: "12.5px",
    color: "#9ca3af",
    fontWeight: 600,
    marginTop: "8px",
    marginBottom: "28px",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "18px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "17px",
    fontWeight: 800,
    color: "#21284d",
    marginBottom: "14px",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#5c6480",
    fontSize: "14px",
    lineHeight: 1.9,
  },
  contactText: {
    margin: 0,
    fontSize: "14px",
    lineHeight: 1.8,
    color: "#5c6480",
    marginBottom: "14px",
  },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: 0,
  },
};

export default PrivacyPolicy;
